import { Box, Button, Typography } from '@mui/material';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import RMFConfigTable from '../../../RFMConfig/components/RFMConfigTable/RFMConfigTable';
import Segment from '../../../RFMConfig/interfaces/Segment';
import { useState } from 'react';
import RMFConfigDialog from '../../../RFMConfig/components/RFMConfigDialog/RFMConfigDialog';
import { isEmpty, omit } from 'lodash';
import useHierarchyRFMConfig from '../../hooks/useHierarchyRFMConfig';
import { updateRFMConfig } from '../../../RFMConfig/services';
import useUser from '../../../../hooks/useUser';
import { RFMConfigLevel } from '../../../RFMConfig/interfaces/RFMConfigLevel';
import toast from 'react-hot-toast';
import TenantField from '../../../../interfaces/TenantField';
import { HierarchyRFMConfigType } from '../../interfaces/HierarchyRFMConfigType';

type HierarchyConfigEditProps = {
  hierarchyValue: string;
  setEditing: (value: string | null) => void;
  segments: Segment[];
};

const HierarchyConfigEdit = ({
  hierarchyValue,
  setEditing,
  segments,
}: HierarchyConfigEditProps) => {
  const { selectedBusiness } = useUser();
  const { hierarchyRFMConfig, setHierarchyRFMConfig, selectedHierarchy } =
    useHierarchyRFMConfig();

  const [isAddingSegment, setIsAddingSegment] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const saveConfig = (config: HierarchyRFMConfigType) => {
    setIsUpdating(true);
    updateRFMConfig(selectedBusiness.id, {
      level: RFMConfigLevel.PRODUCT_HIERARCHY,
      value: config,
    })
      .then((data) => setHierarchyRFMConfig(data.data))
      .catch(() => toast.error('Unable to update config, try again later'))
      .finally(() => setIsUpdating(false));
  };

  const createSegment = async (segment: Segment) => {
    const newConfig = {
      field: (selectedHierarchy as TenantField).key,
      value: {
        ...hierarchyRFMConfig?.value,
        [hierarchyValue]: hierarchyRFMConfig?.value?.[hierarchyValue]
          ? [...(hierarchyRFMConfig?.value?.[hierarchyValue] ?? []), segment]
          : [segment],
      },
    };
    saveConfig(newConfig);
  };

  const updateConfig = async (segments: Segment[]) => {
    const newConfig = {
      field: (selectedHierarchy as TenantField).key,
      value: isEmpty(segments)
        ? omit({ ...hierarchyRFMConfig?.value }, hierarchyValue)
        : { ...hierarchyRFMConfig?.value, [hierarchyValue]: segments },
    };
    saveConfig(newConfig);
  };

  return (
    <Box>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        mb={'1rem'}
      >
        <Button
          startIcon={<ArrowBackIosIcon />}
          onClick={() => setEditing(null)}
          variant='text'
          color='secondary'
        >
          Back
        </Button>
        <Typography variant='h6'>{hierarchyValue}</Typography>
        <Button onClick={() => setIsAddingSegment(true)}>Add Segment</Button>
      </Box>
      <RMFConfigTable
        segments={segments}
        loading={isUpdating}
        onUpdateConfig={updateConfig}
      />
      <RMFConfigDialog
        segments={segments}
        open={isAddingSegment}
        onUpdate={createSegment}
        onClose={setIsAddingSegment.bind(null, false)}
      />
    </Box>
  );
};

export default HierarchyConfigEdit;
